import { locateRow } from "../find";
import { canGroupRows } from "../mutations/group-rows";
import { canDeleteRow } from "../row-policy";
import type { DragData } from "../dnd/types";
import type { EmailDocument } from "../types";
import {
  resolveRowSiblingList,
  siblingListsEqual,
  type RowSiblingList,
} from "./row-selection-gesture";

function areIndicesContiguous(indices: number[]): boolean {
  if (indices.length <= 1) {
    return true;
  }

  const sorted = [...indices].sort((left, right) => left - right);
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index] !== sorted[index - 1] + 1) {
      return false;
    }
  }
  return true;
}

export function rowIdsInDragItem(
  drag: Extract<DragData, { kind: "item-row" }>,
): string[] {
  return drag.rowIds?.length ? drag.rowIds : [drag.rowId];
}

export type MultiSelectRowCapabilities = {
  canDuplicate: boolean;
  canDelete: boolean;
  canGroup: boolean;
  canUp: boolean;
  canDown: boolean;
  showMoveButtons: boolean;
  upLabel: string;
  downLabel: string;
};

function rowMoveCapabilities(
  siblingRowIds: string[],
  orderedRowIds: string[],
): Pick<
  MultiSelectRowCapabilities,
  "canUp" | "canDown" | "showMoveButtons" | "upLabel" | "downLabel"
> {
  const indices = orderedRowIds.map(rowId => siblingRowIds.indexOf(rowId));
  if (indices.some(index => index < 0)) {
    return {
      canUp: false,
      canDown: false,
      showMoveButtons: false,
      upLabel: "Move up",
      downLabel: "Move down",
    };
  }

  if (!areIndicesContiguous(indices)) {
    return {
      canUp: false,
      canDown: false,
      showMoveButtons: false,
      upLabel: "Move up",
      downLabel: "Move down",
    };
  }

  const minIndex = Math.min(...indices);
  const maxIndex = Math.max(...indices);
  return {
    canUp: minIndex > 0,
    canDown: maxIndex < siblingRowIds.length - 1,
    showMoveButtons: true,
    upLabel: "Move up",
    downLabel: "Move down",
  };
}

export function resolveOrderedSiblingRowIds(
  document: EmailDocument,
  rowIds: string[],
): { list: RowSiblingList; rowIds: string[] } | null {
  if (rowIds.length === 0) {
    return null;
  }

  const list = resolveRowSiblingList(document, rowIds[0]);
  if (list === null) {
    return null;
  }

  const selected = new Set(rowIds);
  const ordered = list.rowIds.filter(id => selected.has(id));
  if (ordered.length !== rowIds.length) {
    return null;
  }

  for (const rowId of rowIds) {
    const rowList = resolveRowSiblingList(document, rowId);
    if (rowList === null || !siblingListsEqual(list, rowList)) {
      return null;
    }
  }

  return { list, rowIds: ordered };
}

export function resolveOrderedTopLevelRowIds(
  document: EmailDocument,
  rowIds: string[],
): { rowIds: string[] } | null {
  const resolved = resolveOrderedSiblingRowIds(document, rowIds);
  if (!resolved || resolved.list.parent !== "document") {
    return null;
  }
  return { rowIds: resolved.rowIds };
}

export function isContiguousSiblingRowSelection(
  document: EmailDocument,
  rowIds: string[],
): boolean {
  if (rowIds.length <= 1) {
    return true;
  }

  const resolved = resolveOrderedSiblingRowIds(document, rowIds);
  if (!resolved) {
    return false;
  }

  const indices = resolved.rowIds.map(rowId =>
    resolved.list.rowIds.indexOf(rowId),
  );
  if (indices.some(index => index < 0)) {
    return false;
  }

  return areIndicesContiguous(indices);
}

export function resolveMovableGroupRowIds(
  document: EmailDocument,
  selectedRowIds: readonly string[],
  activeRowId: string,
): string[] {
  if (
    !selectedRowIds.includes(activeRowId) ||
    selectedRowIds.length <= 1
  ) {
    return [activeRowId];
  }

  const ordered = resolveOrderedSiblingRowIds(document, [...selectedRowIds]);
  if (!ordered) {
    return [activeRowId];
  }

  if (
    !ordered.rowIds.includes(activeRowId) ||
    !isContiguousSiblingRowSelection(document, ordered.rowIds)
  ) {
    return [activeRowId];
  }

  return ordered.rowIds;
}

export function getMultiSelectRowCapabilities(
  document: EmailDocument,
  selectedRowIds: readonly string[],
): MultiSelectRowCapabilities | null {
  if (selectedRowIds.length <= 1) {
    return null;
  }

  const ordered = resolveOrderedSiblingRowIds(document, [...selectedRowIds]);
  if (!ordered) {
    return null;
  }

  const move = rowMoveCapabilities(ordered.list.rowIds, ordered.rowIds);

  if (ordered.list.parent === "group") {
    return {
      canDuplicate: true,
      canDelete: ordered.rowIds.every(rowId => canDeleteRow(document, rowId)),
      canGroup: false,
      ...move,
    };
  }

  let canDeleteAll = true;
  const indices: number[] = [];
  for (const rowId of ordered.rowIds) {
    const located = locateRow(document.sections, rowId);
    if (!located || located.location.parent !== "document") {
      return null;
    }
    if (!canDeleteRow(document, rowId)) {
      canDeleteAll = false;
    }
    indices.push(located.location.sectionIndex);
  }

  const uniqueCount = new Set(indices).size;

  return {
    canDuplicate: true,
    canDelete: canDeleteAll && document.sections.length > uniqueCount,
    canGroup: canGroupRows(document, ordered.rowIds),
    ...move,
  };
}
